import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Container,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Stack,
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
  TextField,
  Rating,
} from "@mui/material";
import {
  CalendarMonthOutlined,
  AccessTime,
  Person,
  ContentCut,
  Cancel,
  AccountCircle,
  Logout,
  CalendarToday,
  CheckCircleOutline,
  Edit,
  RateReview,
  Star,
  ArrowBack, // Add this import
} from "@mui/icons-material";
import moment from "moment";
import axios from "axios";
import { MyDomain } from "../../App";
import { useAppContext } from "../../AppContext";
import { HubConnectionBuilder, LogLevel, signalR } from "@microsoft/signalr";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom"; // Add this import
import MPHomeLoginRequired from "../BarberMainPageComps/MPHomeLoginRequired";

export default function ClientAppointments() {
  const { t } = useTranslation();
  const { User, setUser, AllData, Lang } = useAppContext();
  const history = useHistory(); // Add this

  // Add this helper function at the top of your component
  const getCurrencySymbol = (countryCode) => {
    const currencyMap = {
      "+972": "₪", // Israel - Shekel
      "+46": "kr", // Sweden - Krona
      "+1": "$", // US/Canada - Dollar
      "+44": "£", // UK - Pound
      "+49": "€", // Germany - Euro
      // Add more as needed
    };

    return currencyMap[countryCode] || "₪"; // Default to shekel
  };

  // Then use it in your component
  const currencySymbol = getCurrencySymbol(User?.countryNumber);

  const [isLoading, setIsLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    appointmentId: null,
  });
  const [BarberPhoneNumber, setBarberPhoneNumber] = useState("");

  // Add state for edit name dialog
  const [editNameDialog, setEditNameDialog] = useState({
    open: false,
    newName: "",
  });
  const [isUpdatingName, setIsUpdatingName] = useState(false);

  // Add state for review dialog
  const [reviewDialog, setReviewDialog] = useState({
    open: false,
    appointmentId: null,
    barberPhoneNumber: "",
    rating: 0,
    comment: "",
  });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Add handleBack function
  const handleBack = () => {
    history.goBack();
  };

  const GetMyApps = async () => {
    if (!User || !User.phoneNumber) {
      return;
    }

    const data = {
      PhoneNumber: User.phoneNumber,
    };

    try {
      const response = await axios.post(
        `${MyDomain}Users2/GetUserAppsAndServices`,
        data,
      );
      console.log(response.data);
      setAppointments(response.data.myAppointments || []);
    } catch (err) {
      console.error(err);
    } finally {
    }
  };

  const handleCancelClick = (appointmentId, barberPhoneNumber) => {
    setCancelDialog({ open: true, appointmentId });
    setBarberPhoneNumber(barberPhoneNumber);
  };

  const handleCancelConfirm = async () => {
    try {
      // ✅ Fetch the barber's data to get the correct expo token
      let barberExpoToken = null;

      try {
        const barberDataResponse = await axios.post(
          `${MyDomain}Barbers2/GetAllBarberData`,
          { PhoneNumber: BarberPhoneNumber },
        );

        console.log("📱 Barber data fetched:", barberDataResponse.data);

        // Get the expo token from the fetched data
        if (barberDataResponse.data?.barberData?.expoToken) {
          barberExpoToken = barberDataResponse.data.barberData.expoToken;
        }

        // If the appointment belongs to a worker (not the owner)
        if (barberDataResponse.data?.allBarbersWithData) {
          const appointmentBarber =
            barberDataResponse.data.allBarbersWithData.find(
              (b) => b.barber.phoneNumber === BarberPhoneNumber,
            );
          if (appointmentBarber?.barber?.expoToken) {
            barberExpoToken = appointmentBarber.barber.expoToken;
          }
        }
      } catch (fetchError) {
        console.error("Error fetching barber data:", fetchError);
        // Continue anyway - notification is optional
      }

      console.log("📱 Sending cancellation notification to barber:", {
        barberPhone: BarberPhoneNumber,
        expoToken: barberExpoToken,
      });

      const data = {
        Appointment: {
          Id: cancelDialog.appointmentId,
          Name: null,
          PhoneNumber: null,
          IsTaken: 0,
          BarberPhoneNumber: BarberPhoneNumber,
        },
        Barber: {
          ExpoToken: barberExpoToken, // ✅ Use the fetched barber's token
        },
        UserName: User.name,
        Lang: Lang,
        BookedServices: [],
        AllServices: [],
      };

      await axios.put(
        `${MyDomain}Appointments2/UpdateAppWithServicesForDelete`,
        data,
      );

      setCancelDialog({ open: false, appointmentId: null });
      GetMyApps();
    } catch (err) {
      console.error("Error canceling appointment:", err);
      alert("Failed to cancel appointment. Please try again.");
    }
  };
  const handleCancelClose = () => {
    setCancelDialog({ open: false, appointmentId: null });
  };

  // Add handlers for edit name
  const handleEditNameClick = () => {
    setEditNameDialog({
      open: true,
      newName: User.name || "",
    });
  };

  const handleEditNameClose = () => {
    setEditNameDialog({
      open: false,
      newName: "",
    });
  };

  const handleUpdateName = async () => {
    if (!editNameDialog.newName.trim()) {
      return;
    }

    setIsUpdatingName(true);

    const data = {
      Id: User.id,
      Name: editNameDialog.newName.trim(),
    };

    try {
      await axios.put(`${MyDomain}Users2/UpdateUserName`, data);

      // Update local User state
      const updatedUser = {
        ...User,
        name: editNameDialog.newName.trim(),
      };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      handleEditNameClose();
    } catch (err) {
      console.error("Error updating name:", err);
      alert(t("ErrorUpdatingName") || "Error updating name");
    } finally {
      setIsUpdatingName(false);
    }
  };

  // Add handlers for review
  const handleReviewClick = (appointmentId, barberPhoneNumber) => {
    setReviewDialog({
      open: true,
      appointmentId,
      barberPhoneNumber,
      rating: 0,
      comment: "",
    });
  };

  const handleReviewClose = () => {
    setReviewDialog({
      open: false,
      appointmentId: null,
      barberPhoneNumber: "",
      rating: 0,
      comment: "",
    });
  };

  const handleSubmitReview = async () => {
    if (reviewDialog.rating === 0) {
      alert(t("PleaseSelectRating") || "Please select a rating");
      return;
    }

    setIsSubmittingReview(true);

    const data = {
      BarberPhoneNumber: AllData.barberData.phoneNumber,
      UserName: User.name,
      UserPhoneNumber: User.phoneNumber,
      Comment: reviewDialog.comment.trim(),
      Rating: reviewDialog.rating,
      AppointmentId: reviewDialog.appointmentId,
    };
    try {
      await axios.post(`${MyDomain}Reviews/CreateReview`, data);

      // Refresh appointments to update addReview flag
      await GetMyApps();

      handleReviewClose();

      // Show success message
      alert(t("ReviewSubmitted") || "Thank you for your review!");
    } catch (err) {
      console.error("Error submitting review:", err);
      alert(
        t("ErrorSubmittingReview") ||
          "Error submitting review. Please try again.",
      );
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const calculateTotalPrice = (services) => {
    return services.reduce(
      (total, service) => total + parseFloat(service.price),
      0,
    );
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    setAppointments([]);
    console.log("User logged out");
  };

  useEffect(() => {
    if (User && User.phoneNumber) {
      GetMyApps();
    }
  }, [User]);

  const handleRealTimeUpdate = (data) => {
    console.log("🔥 REAL TIME BOOKING UPDATE:", data);
    loadAppointments();
  };

  const loadAppointments = () => {
    console.log("Loading appointments...");
  };

  useEffect(() => {
    if (!User || !User.phoneNumber) {
      console.log("⚠️ No user logged in");
      return;
    }

    console.log("🔄 Setting up SignalR for user:", User.phoneNumber);

    const connection = new HubConnectionBuilder()
      .withUrl(`${MyDomain}appointmentHub`)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    const startConnection = async () => {
      try {
        await connection.start();
        console.log("✅ SignalR connected!");

        await GetMyApps();

        connection.on("ReceiveBookingUpdate", (data) => {
          console.log("📩 Booking update received:", data);

          if (
            data?.type === "booking_created" ||
            data?.type === "booking_cancelled"
          ) {
            console.log("🔄 Refreshing user appointments...");
            GetMyApps();
          }
        });
      } catch (error) {
        console.log("❌ SignalR error:", error);
        setTimeout(startConnection, 3000);
      }
    };

    startConnection();

    return () => {
      connection.off("ReceiveBookingUpdate");
      connection.stop();
    };
  }, [User]);

  if (!User) {
    return (
      <MPHomeLoginRequired
        title={t("PleaseLoginToView")}
        subtitle={t("SignInToAccess")}
        onLoginSuccess={GetMyApps}
      />
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="70vh"
      >
        <CircularProgress size={50} thickness={4} />
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mt: 2, fontWeight: 500 }}
        >
          {t("LoadingYourAppointments")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        pb: isMobile ? 3 : 4,
        paddingTop: 8,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
        }}
      >
        {/* Compact Mobile Header with Back Button */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 0.5 }}
          >
            <IconButton
              onClick={handleBack}
              sx={{
                bgcolor: "rgba(102, 126, 234, 0.1)",
                color: "#667eea",
                "&:hover": {
                  bgcolor: "rgba(102, 126, 234, 0.2)",
                },
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
              }}
              size={isMobile ? "small" : "medium"}
            >
              <ArrowBack fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight="700"
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("MyAppointments")}
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500, pl: { xs: 5.5, sm: 6.5 } }}
          >
            {t("ManageYourBookings")}
          </Typography>
        </Box>

        {/* Enhanced User Info Card with Edit Name Button */}
        {User && (
          <Card
            sx={{
              mb: { xs: 2, sm: 3 },
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              borderRadius: 2,
            }}
            elevation={0}
          >
            <CardContent sx={{ py: { xs: 2, sm: 2.5 }, px: { xs: 2, sm: 3 } }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      width: { xs: 42, sm: 48 },
                      height: { xs: 42, sm: 48 },
                    }}
                  >
                    <AccountCircle sx={{ fontSize: { xs: 28, sm: 32 } }} />
                  </Avatar>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography
                        variant={isMobile ? "body1" : "h6"}
                        fontWeight="700"
                        sx={{ lineHeight: 1.2 }}
                      >
                        {User.name || t("User")}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleEditNameClick}
                        sx={{
                          color: "white",
                          padding: "4px",
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.2)",
                          },
                        }}
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {User.phoneNumber}
                    </Typography>
                  </Box>
                </Stack>

                {/* Compact Logout Button */}
                {isMobile ? (
                  <IconButton
                    onClick={handleLogout}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.3)",
                      },
                    }}
                    size="medium"
                  >
                    <Logout fontSize="small" />
                  </IconButton>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<Logout />}
                    onClick={handleLogout}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.3)",
                      "&:hover": {
                        borderColor: "white",
                        bgcolor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    {t("Logout")}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Appointments List */}
        {appointments.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 6 },
              textAlign: "center",
              backgroundColor: "white",
              borderRadius: 2,
              border: "2px dashed #e0e0e0",
            }}
          >
            <CalendarToday
              sx={{
                fontSize: { xs: 60, sm: 80 },
                color: "#e0e0e0",
                mb: 2,
              }}
            />
            <Typography
              variant={isMobile ? "h6" : "h5"}
              color="text.primary"
              fontWeight="600"
              gutterBottom
            >
              {t("NoAppointmentsYet")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 300, mx: "auto" }}
            >
              {t("BookYourFirst")}
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={{ xs: 2, sm: 2.5 }}>
            {appointments.map((item) => {
              const { appointment, bookedServices } = item;
              const totalPrice = calculateTotalPrice(bookedServices);
              const isToday = moment(appointment.date, "DD-MM-YYYY").isSame(
                moment(),
                "day",
              );
              const isPast = moment(appointment.date, "DD-MM-YYYY").isBefore(
                moment(),
                "day",
              );
              const canReview = appointment.addReview !== 1; //isPast &&

              return (
                <Card
                  key={appointment.id}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: isToday
                      ? "#10b981"
                      : isPast
                        ? "#e5e7eb"
                        : "#e0e7ff",
                    transition: "all 0.2s ease",
                    "&:active": {
                      transform: "scale(0.98)",
                    },
                  }}
                >
                  {/* Status Bar */}
                  {isToday && (
                    <Box
                      sx={{
                        background:
                          "linear-gradient(90deg, #10b981 0%, #059669 100%)",
                        py: 0.75,
                        px: 2,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        justifyContent="center"
                      >
                        <CheckCircleOutline
                          sx={{ fontSize: 16, color: "white" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "white",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {t("TodaysAppointment")}
                        </Typography>
                      </Stack>
                    </Box>
                  )}

                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* Date & Time Header */}
                    <Box sx={{ mb: 2 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1.5}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CalendarMonthOutlined
                            sx={{ fontSize: 20, color: "#667eea" }}
                          />
                          <Typography
                            variant={isMobile ? "h6" : "h5"}
                            fontWeight="700"
                            sx={{ color: "#1f2937" }}
                          >
                            {moment(appointment.date, "DD-MM-YYYY").format(
                              "MMM DD, YYYY",
                            )}
                          </Typography>
                        </Stack>
                        {isPast && !isToday && (
                          <Chip
                            label={t("Past")}
                            size="small"
                            sx={{
                              backgroundColor: "#f3f4f6",
                              color: "#6b7280",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                            }}
                          />
                        )}
                      </Stack>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ pl: 3.5 }}
                      >
                        <AccessTime sx={{ fontSize: 16, color: "#9ca3af" }} />
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          color="text.secondary"
                        >
                          {appointment.timeStart} - {appointment.timeEnd}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#9ca3af",
                            px: 1,
                            py: 0.25,
                            bgcolor: "#f3f4f6",
                            borderRadius: 1,
                          }}
                        >
                          {appointment.timeLength} {t("Min")}
                        </Typography>
                      </Stack>
                    </Box>

                    {/* Services Section - Mobile Optimized */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        mb: 2,
                        backgroundColor: "#fafbfc",
                        borderColor: "#e5e7eb",
                        borderRadius: 1.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="700"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          display: "block",
                          mb: 1,
                        }}
                      >
                        {t("Services")}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />

                      <Stack spacing={1}>
                        {bookedServices.map((service, index) => (
                          <Stack
                            key={service.id}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              py: 0.75,
                              borderBottom:
                                index !== bookedServices.length - 1
                                  ? "1px solid #f3f4f6"
                                  : "none",
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <ContentCut
                                sx={{ fontSize: 16, color: "#667eea" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {service.service}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              fontWeight="700"
                              sx={{ color: "#667eea" }}
                            >
                              {service.price} {currencySymbol}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Total */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          bgcolor: "#eff6ff",
                          p: 1.5,
                          borderRadius: 1,
                          mx: -1.5,
                          mb: -1.5,
                          mt: 1,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="700">
                          {t("Total")}
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="700"
                          sx={{ color: "#667eea" }}
                        >
                          {totalPrice} {currencySymbol}
                        </Typography>
                      </Stack>
                    </Paper>

                    {/* Barber Info */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        mb: 2,
                        p: 1.5,
                        bgcolor: "#f9fafb",
                        borderRadius: 1,
                      }}
                    >
                      <Person sx={{ fontSize: 18, color: "#6b7280" }} />
                      <Typography variant="body2" color="text.secondary">
                        {t("Barber")} {appointment.barberPhoneNumber}
                      </Typography>
                    </Stack>

                    {/* Action Buttons */}
                    <Stack spacing={1.5}>
                      {/* Cancel Button - Touch Optimized */}
                      {!isPast && (
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<Cancel />}
                          onClick={() =>
                            handleCancelClick(
                              appointment.id,
                              appointment.barberPhoneNumber,
                            )
                          }
                          sx={{
                            py: { xs: 1.5, sm: 1.75 },
                            fontWeight: 700,
                            textTransform: "none",
                            bgcolor: "#ef4444",
                            boxShadow: "0 4px 12px rgba(239, 68, 68, 0.2)",
                            "&:hover": {
                              bgcolor: "#dc2626",
                              boxShadow: "0 6px 16px rgba(239, 68, 68, 0.3)",
                            },
                            "&:active": {
                              transform: "scale(0.98)",
                            },
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                          }}
                        >
                          {t("CancelAppointment")}
                        </Button>
                      )}

                      {/* Review Button */}
                      {canReview && (
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<RateReview />}
                          onClick={() =>
                            handleReviewClick(
                              appointment.id,
                              appointment.barberPhoneNumber,
                            )
                          }
                          sx={{
                            py: { xs: 1.5, sm: 1.75 },
                            fontWeight: 700,
                            textTransform: "none",
                            bgcolor: "#f59e0b",
                            boxShadow: "0 4px 12px rgba(245, 158, 11, 0.2)",
                            "&:hover": {
                              bgcolor: "#d97706",
                              boxShadow: "0 6px 16px rgba(245, 158, 11, 0.3)",
                            },
                            "&:active": {
                              transform: "scale(0.98)",
                            },
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                          }}
                        >
                          {t("LeaveReview") || "Leave a Review"}
                        </Button>
                      )}

                      {/* Already Reviewed Indicator */}
                      {isPast && appointment.addReview === 1 && (
                        <Paper
                          sx={{
                            p: 1.5,
                            bgcolor: "#ecfdf5",
                            border: "1px solid #a7f3d0",
                            borderRadius: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            justifyContent="center"
                          >
                            <CheckCircleOutline
                              sx={{ fontSize: 18, color: "#10b981" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: "#059669", fontWeight: 600 }}
                            >
                              {t("ReviewSubmitted") || "Review Submitted"}
                            </Typography>
                          </Stack>
                        </Paper>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}

        {/* Cancel Confirmation Dialog - Mobile Optimized */}
        <Dialog
          open={cancelDialog.open}
          onClose={handleCancelClose}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              mx: 2,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight="700">
              {t("CancelAppointmentQuestion")}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: "text.secondary" }}>
              {t("CancelConfirmation")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              onClick={handleCancelClose}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                flex: 1,
                py: 1.25,
              }}
            >
              {t("KeepIt")}
            </Button>
            <Button
              onClick={handleCancelConfirm}
              variant="contained"
              color="error"
              autoFocus
              sx={{
                textTransform: "none",
                fontWeight: 600,
                flex: 1,
                py: 1.25,
              }}
            >
              {t("YesCancel")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Name Dialog */}
        <Dialog
          open={editNameDialog.open}
          onClose={handleEditNameClose}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              mx: 2,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight="700">
              {t("EditName") || "Edit Name"}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              label={t("Name") || "Name"}
              value={editNameDialog.newName}
              onChange={(e) =>
                setEditNameDialog({
                  ...editNameDialog,
                  newName: e.target.value,
                })
              }
              variant="outlined"
              disabled={isUpdatingName}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "#667eea",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#667eea",
                  },
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              onClick={handleEditNameClose}
              variant="outlined"
              disabled={isUpdatingName}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                flex: 1,
                py: 1.25,
              }}
            >
              {t("Cancel") || "Cancel"}
            </Button>
            <Button
              onClick={handleUpdateName}
              variant="contained"
              disabled={isUpdatingName || !editNameDialog.newName.trim()}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                flex: 1,
                py: 1.25,
                bgcolor: "#667eea",
                "&:hover": {
                  bgcolor: "#5568d3",
                },
              }}
            >
              {isUpdatingName ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("Save") || "Save"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Review Dialog */}
        <Dialog
          open={reviewDialog.open}
          onClose={handleReviewClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              mx: 2,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight="700">
              {t("LeaveReview") || "Leave a Review"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("ShareYourExperience") ||
                "Share your experience with this service"}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3}>
              {/* Rating Section */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="600"
                  gutterBottom
                  sx={{ mb: 1.5 }}
                >
                  {t("Rating") || "Rating"} *
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    p: 2,
                    bgcolor: "#f9fafb",
                    borderRadius: 2,
                  }}
                >
                  <Rating
                    name="rating"
                    value={reviewDialog.rating}
                    onChange={(event, newValue) => {
                      setReviewDialog({
                        ...reviewDialog,
                        rating: newValue,
                      });
                    }}
                    size="large"
                    sx={{
                      fontSize: { xs: "2.5rem", sm: "3rem" },
                      "& .MuiRating-iconFilled": {
                        color: "#f59e0b",
                      },
                      "& .MuiRating-iconHover": {
                        color: "#d97706",
                      },
                    }}
                  />
                </Box>
                {reviewDialog.rating > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", textAlign: "center", mt: 1 }}
                  >
                    {reviewDialog.rating === 1 && (t("Poor") || "Poor")}
                    {reviewDialog.rating === 2 && (t("Fair") || "Fair")}
                    {reviewDialog.rating === 3 && (t("Good") || "Good")}
                    {reviewDialog.rating === 4 &&
                      (t("VeryGood") || "Very Good")}
                    {reviewDialog.rating === 5 &&
                      (t("Excellent") || "Excellent")}
                  </Typography>
                )}
              </Box>

              {/* Comment Section */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="600"
                  gutterBottom
                  sx={{ mb: 1 }}
                >
                  {t("Comment") || "Comment"} ({t("Optional") || "Optional"})
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={
                    t("ShareYourThoughts") ||
                    "Share your thoughts about the service..."
                  }
                  value={reviewDialog.comment}
                  onChange={(e) =>
                    setReviewDialog({
                      ...reviewDialog,
                      comment: e.target.value,
                    })
                  }
                  variant="outlined"
                  disabled={isSubmittingReview}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#f59e0b",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f59e0b",
                      },
                    },
                  }}
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              onClick={handleReviewClose}
              variant="outlined"
              disabled={isSubmittingReview}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                flex: 1,
                py: 1.25,
              }}
            >
              {t("Cancel") || "Cancel"}
            </Button>
            <Button
              onClick={handleSubmitReview}
              variant="contained"
              disabled={isSubmittingReview || reviewDialog.rating === 0}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                flex: 1,
                py: 1.25,
                bgcolor: "#f59e0b",
                "&:hover": {
                  bgcolor: "#d97706",
                },
              }}
            >
              {isSubmittingReview ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("Submit") || "Submit Review"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
